import { useMemo } from 'react';
import {
  intlNumberFormatter,
  type IntlNumberFormatterOptions,
} from 'react-formatted-input-hook';

import { useNumberLocalize } from './locales/useNumberLocalize.js';

type Params = {
  /**
   * Optional options to customize the number formatter.
   * @param minimumFractionDigits - The minimum number of fraction digits to display. Defaults to 0.
   * @param maximumFractionDigits - The maximum number of fraction digits to display. Defaults to 2.
   */
  options?: Omit<
    IntlNumberFormatterOptions,
    'liveUpdate' | 'onChange' | 'value'
  >;
};

/**
 * A hook that returns a number formatter function based on the user's locale and formatting preferences.
 * @param options - Optional options to customize the number formatter.
 * @returns A number formatter function that can be used to format numbers.
 */
export const useNumberFormatter = () => {
  const locale = useNumberLocalize();

  return useMemo(() => {
    return {
      format: (value: string | number, params: Params = {}) => {
        const formatter = intlNumberFormatter({
          locales: locale,
          ...params.options,
        });

        return formatter.format?.(formatter.onBlur?.(String(value)) ?? '');
      },
    };
  }, [locale]);
};
